import React from "react";
import cn from "./helpers/cn";

interface DividerProps extends React.HTMLAttributes<HTMLElement> {
  component?: React.ElementType;
  orientation?: "horizontal" | "vertical";
  variant?: "fullWidth" | "inset" | "middle";
  light?: boolean;
  flexItem?: boolean;
  absolute?: boolean;
}

const Divider = React.forwardRef<HTMLElement, DividerProps>((props, ref) => {
  const {
    className,
    component,
    orientation = "horizontal",
    variant = "fullWidth",
    light = false,
    flexItem = false,
    absolute = false,
    ...other
  } = props;

  // Vertical não pode usar <hr> (semântica HTML), troca para div.
  const Component = component ?? (orientation === "vertical" ? "div" : "hr");
  const isHorizontal = orientation === "horizontal";

  return (
    <Component
      ref={ref}
      role={Component === "hr" ? undefined : "separator"}
      aria-orientation={!isHorizontal ? "vertical" : undefined}
      className={cn(
        // Base do MUI v4: 1px de espessura, cor cinza, sem borda no <hr>.
        "shrink-0 border-0",
        isHorizontal
          ? "h-px w-full"
          : "h-auto self-stretch w-px",
        light ? "bg-slate-200/60" : "bg-slate-200",

        absolute && isHorizontal && "absolute bottom-0 left-0 w-full",

        // inset: padding-left 72px (alinha com o conteúdo após o ListItemIcon).
        variant === "inset" && isHorizontal && "ml-[72px] w-[calc(100%-72px)]",

        // middle: margens laterais 16px.
        variant === "middle" && isHorizontal && "mx-4 w-[calc(100%-32px)]",
        variant === "middle" && !isHorizontal && "my-2 h-[calc(100%-16px)]",

        // flexItem: deixa o divider se comportar como item flex.
        flexItem && (isHorizontal ? "self-stretch" : "h-auto"),

        className
      )}
      {...other}
    />
  );
});

Divider.displayName = "Divider";

export default Divider;
